import { sleep } from '@sports-utils/timer';
import { useThrottleFn, useTimeoutFn } from '@vueuse/core';
import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref, watch, watchEffect } from 'vue';
import type { IAsiaBetSlipContext } from '@/components/betSlip/betSlipContext/BaseBetSlipContext';
import {
    cancelTicketMillisecond,
    useBaseBetSlipContext,
} from '@/components/betSlip/betSlipContext/BaseBetSlipContext';
import type {
    PlaceBetErrorHandler,
    PlaceDynamicMarketBetErrorRules,
} from '@/components/betSlip/betSlipContext/usePlaceBetErrorHandler';
import { usePlaceBetErrorHandler } from '@/components/betSlip/betSlipContext/usePlaceBetErrorHandler';
import { usePlatform } from '@/composable/usePlatform';
import { Api } from '@/core/lib/api';
import { i18n } from '@/core/lib/i18n';
import { getMaxPayout, getOpenTicketErrorMessage, getPlaceBetErrorMessage, getVoucherMaxPayout } from '@/core/lib/ticket';
import { isVoucherValidForOdds } from '@/core/lib/voucherHelper';
import { useSelectionSubscriptionCallback } from '@/core/oddsApi/composable/useSelectionSubscriptionCallback';
import type { IDynamicMarketBetSlip } from '@/interface/IDynamicMarketBetSlip';
import {
    OpenTicketStatus,
    PlaceOrderStatus,
    SiteStyle,
} from '@/interface/enum';
import { useBetSlipStore } from '@/store/betSlipStore';
import { useCustomerStore } from '@/store/customerStore';
import { useLayoutStore } from '@/store/layoutStore';
import { useToggleStore } from '@/store/toggleStore';

export interface IDynamicMarketBetSlipContext extends IAsiaBetSlipContext {
    dynamicMarketBetSlip: Ref<IDynamicMarketBetSlip>;
}

export const createDynamicMarketBetSlipContext = (dynamicMarketBetSlip: Ref<IDynamicMarketBetSlip>): IDynamicMarketBetSlipContext => {
    const betSlipContext = useBaseBetSlipContext(dynamicMarketBetSlip);

    const {
        stakeInput,
        setBetSlipMessage,
        autoRefresh,
        emitPlaceBetSuccess,
        vouchers,
        setVouchers,
        selectedVoucher,
        setSelectedVoucher,
        voucherEnabled,
        setVoucherEnabled,
    } = betSlipContext;

    const { removeDynamicMarketBetSlip, updateDynamicMarketBetSlip } = useBetSlipStore();
    const { singleLastStake, isShowLastStake } = storeToRefs(useBetSlipStore());
    const { setSingleLastStake } = useBetSlipStore();
    const { placeBetDelayTime, isVoucherEnabled } = storeToRefs(useToggleStore(SiteStyle.Asia));
    const { refreshBalanceAfterAction } = usePlatform();
    const { formatMoneyWithDecimal } = useCustomerStore();

    const maxPayout = computed<string>(() => {
        const price = dynamicMarketBetSlip.value.selection.price.value;
        const priceStyle = dynamicMarketBetSlip.value.selection.price.priceStyle;

        const payout = selectedVoucher.value !== null
            ? getVoucherMaxPayout(selectedVoucher.value.stake, price, priceStyle)
            : getMaxPayout(stakeInput.stake.value, price, priceStyle);
        return formatMoneyWithDecimal(payout);
    });

    const isVoucherAvailable = computed(() => vouchers.value.length !== 0);
    const isVoucherValidForTicket = computed(() => isVoucherValidForOdds(
        dynamicMarketBetSlip.value.selection.price.value,
        dynamicMarketBetSlip.value.selection.price.priceStyle,
    ));

    watchEffect(() => {
        if (!isVoucherValidForTicket.value) {
            setVoucherEnabled(false);
            setSelectedVoucher(null);
        }
    });

    const getVouchers = async () => {
        if (!isVoucherEnabled.value) return [];
        try {
            return await Api.getVouchers(
                dynamicMarketBetSlip.value.display.sportType,
                dynamicMarketBetSlip.value.leagueId,
            );
        } catch {
            return [];
        }
    };

    let cancelToken: CancelTokenSource | undefined;

    const cancelTicket = (_key?: string) => {
        removeDynamicMarketBetSlip();
    };

    const openTicket = async (_shouldUpdateStake = false) => {
        try {
            if (cancelToken) cancelToken.cancel('superseded openTicket');
            cancelToken = axios.CancelToken.source();

            const [response, voucherResponse] = await Promise.all([
                Api.openDynamicMarketTicket(
                    dynamicMarketBetSlip.value.selectionId,
                    cancelToken.token,
                ),
                getVouchers(),
            ]);

            if (response.errorCode !== OpenTicketStatus.Success) {
                setBetSlipMessage(getOpenTicketErrorMessage(response.errorCode));
                if (response.errorCode !== OpenTicketStatus.MaxOddsExceed) {
                    useTimeoutFn(() => cancelTicket(), cancelTicketMillisecond);
                }
                return;
            }

            if (!response.selection) return;

            const { price, betCondition, score } = response.selection;

            updateDynamicMarketBetSlip(current => ({
                ...current,
                uid: response.uid,
                minBet: response.minBet,
                maxBet: response.maxBet,
                balance: response.balance,
                selection: {
                    price,
                    betCondition: betCondition || current.selection.betCondition,
                    score: score ?? current.selection.score,
                },
            }));

            setVouchers(voucherResponse);
        } catch (error) {
            if (axios.isCancel(error)) return;
            setBetSlipMessage(i18n.t('bet_slip_error_message_general_failure'));
            useTimeoutFn(() => cancelTicket(), cancelTicketMillisecond);
        }
    };

    const openTicketThrottled = useThrottleFn(openTicket, 1000, true, false);

    autoRefresh(() => openTicketThrottled());

    const subscribeOdds = () => {
        const { onUpdated, onDeleted } = useSelectionSubscriptionCallback({
            marketId: ref(dynamicMarketBetSlip.value.marketId),
            selectionId: ref(dynamicMarketBetSlip.value.selectionId),
            sportType: dynamicMarketBetSlip.value.display.sportType,
            leagueSize: dynamicMarketBetSlip.value.leagueSize,
            leagueId: dynamicMarketBetSlip.value.leagueId,
        });

        onUpdated(() => openTicketThrottled());

        onDeleted(() => {
            setBetSlipMessage(i18n.t('bet_slip_error_message_odds_closed'));
            useTimeoutFn(cancelTicket, cancelTicketMillisecond);
        });
    };

    watch(() => dynamicMarketBetSlip.value.uid, (uid) => {
        if (!uid) {
            openTicket();
            stakeInput.setStake(isShowLastStake.value ? singleLastStake.value : 0);
        }
    }, { immediate: true });

    const { placeBetErrorRuleMap, defaultErrorHandler, errorMessageHandler } = usePlaceBetErrorHandler(betSlipContext);

    const ticketContextChangeHandler: PlaceBetErrorHandler = (_placeOrderStatus: PlaceOrderStatus, _extraInfo: string) => {
        openTicket();
    };

    const retryOpenTicketHandler: PlaceBetErrorHandler = (placeOrderStatus: PlaceOrderStatus, _extraInfo: string) => {
        setBetSlipMessage(getPlaceBetErrorMessage(placeOrderStatus));
        openTicket();
    };

    const invalidEventOrPriceHandler: PlaceBetErrorHandler = (placeOrderStatus: PlaceOrderStatus, _extraInfo: string) => {
        setBetSlipMessage(getPlaceBetErrorMessage(placeOrderStatus));
        useTimeoutFn(() => cancelTicket(), cancelTicketMillisecond);
    };

    const limitExceedErrorHandler: PlaceBetErrorHandler = (_placeOrderStatus: PlaceOrderStatus, extraInfo: string) => {
        const alternateStake = parseInt(extraInfo);
        if (!Number.isNaN(alternateStake)) {
            setBetSlipMessage(i18n.t('bet_slip_error_message_limit_exceed', { alternateStake }));
            stakeInput.setStake(alternateStake);
        } else {
            setBetSlipMessage(getPlaceBetErrorMessage(PlaceOrderStatus.LimitExceedTryLater));
        }
    };

    const invalidVoucherHandler: PlaceBetErrorHandler = (placeOrderStatus: PlaceOrderStatus, _extraInfo: string) => {
        setBetSlipMessage(getPlaceBetErrorMessage(placeOrderStatus));
        openTicket();
    };

    const placeDynamicMarketBetErrorRuleMap: Partial<Record<PlaceDynamicMarketBetErrorRules, PlaceBetErrorHandler>> = {
        ...placeBetErrorRuleMap,
        [PlaceOrderStatus.RetryOpenTicket]: retryOpenTicketHandler,
        [PlaceOrderStatus.PriceChange]: ticketContextChangeHandler,
        [PlaceOrderStatus.HdpPointChange]: ticketContextChangeHandler,
        [PlaceOrderStatus.ScoreChanged]: ticketContextChangeHandler,
        [PlaceOrderStatus.PriceSuspend]: invalidEventOrPriceHandler,
        [PlaceOrderStatus.InvalidPrice]: invalidEventOrPriceHandler,
        [PlaceOrderStatus.EventClosed]: invalidEventOrPriceHandler,
        [PlaceOrderStatus.LimitExceed]: limitExceedErrorHandler,
        [PlaceOrderStatus.LimitExceedTryLater]: errorMessageHandler,
        [PlaceOrderStatus.ThirdPartyTimeout]: errorMessageHandler,
        [PlaceOrderStatus.InvalidVoucher]: invalidVoucherHandler,
        [PlaceOrderStatus.InvalidPriceForVoucher]: invalidVoucherHandler,
    };

    const placeBet = async () => {
        if (!betSlipContext.isPlaceBetEnabled.value) return;
        try {
            betSlipContext.toggleIsPlaceBetInProgress(true);

            const hasVoucher = voucherEnabled.value && selectedVoucher.value !== null;
            const stake = hasVoucher ? selectedVoucher.value!.stake : stakeInput.stake.value;
            const request = {
                uid: dynamicMarketBetSlip.value.uid,
                stake,
                betPage: dynamicMarketBetSlip.value.betPage,
                selectionId: dynamicMarketBetSlip.value.selectionId,
                matchId: dynamicMarketBetSlip.value.matchId,
                ...(hasVoucher ? { voucherId: parseInt(selectedVoucher.value!.id) } : {}),
            };

            const [response] = await Promise.all([
                hasVoucher
                    ? Api.placeDynamicMarketFreeBet(request)
                    : Api.placeDynamicMarketBet(request),
                sleep(placeBetDelayTime.value * 1000),
            ]);

            if (response.isPlaceBetSuccess) {
                const { toggleBetSlipExpanded } = useLayoutStore();
                toggleBetSlipExpanded(false);
                if (isShowLastStake.value && !hasVoucher) setSingleLastStake(stakeInput.stake.value);
                emitPlaceBetSuccess();
                removeDynamicMarketBetSlip();
                refreshBalanceAfterAction();
                return;
            }

            const { placeOrderStatus } = response;
            const handler = placeDynamicMarketBetErrorRuleMap[placeOrderStatus as PlaceDynamicMarketBetErrorRules] ?? defaultErrorHandler;

            handler(placeOrderStatus, response.extraInfo);
        } catch {
            errorMessageHandler(PlaceOrderStatus.GeneralFailure, '');
        } finally {
            betSlipContext.toggleIsPlaceBetInProgress(false);
        }
    };

    return {
        ...betSlipContext,
        dynamicMarketBetSlip,
        maxPayout,
        isVoucherAvailable,
        isVoucherValidForTicket,
        openTicket,
        cancelTicket,
        placeBet,
        subscribeOdds,
    };
};
